import { useLanguage } from '#composables/useLanguage';
import {
  CUSTOMER_SEGMENTATION_TYPES,
  sortFromCompareArray,
  type PerimeterFilterViewType,
  type ProductFilterConfigType,
  type ProductTaxonomyType
} from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { computed, unref, type ComputedRef, type Ref } from 'vue';
import type { TreeViewItemType } from '../../../../../components/treeView';
import type { FilterNameType, FiltersTreeType } from '../../../../../composables/kpisPerimeter';
import { getPerimeterAvailableFilters } from '../../filter.query';
import { useActivityTypeData } from './useActivityTypeData';
import { useAssortmentTypologiesData } from './useAssortmentTypologiesData';
import { useBrandData } from './useBrandData';
import { useBusinessBrandData } from './useBusinessBrandData';
import { useCustomerSegmentationFilterData } from './useCustomerSegmentationFilterData';
import { useHoldingsData } from './useHoldingsData';
import { useIntegratedFranchisedData } from './useIntegratedFranchisedData';
import { useNomenclatureData } from './useNomenclatureData';
import type { TreeView } from './useProductData';
import { useProductData } from './useProductData';
import { useProductTypologiesData } from './useProductTypologiesData';
import { useStoreData } from './useStoreData';
import { useStoresRegionData } from './useStoresRegionData';
import { useSuppliersData } from './useSuppliersData';
import { useTriptyqueData } from './useTriptyqueData';

const { CONSOSTYLE, LIFESTAGE } = CUSTOMER_SEGMENTATION_TYPES;

interface UsePerimeterFiltersDataParams {
  perimeterFilterView?: PerimeterFilterViewType;
  debouncedSearch: Readonly<Ref<string>>;
  checkedItemsState: Ref<FiltersTreeType>;
  taxonomy: Ref<ProductTaxonomyType>;
  productFilterConfig: ProductFilterConfigType;
  isEnabledPredicate?(filterName: FilterNameType): boolean;
}

export const usePerimeterFiltersData = ({
  perimeterFilterView,
  debouncedSearch,
  checkedItemsState,
  taxonomy,
  productFilterConfig,
  isEnabledPredicate
}: UsePerimeterFiltersDataParams) => {
  const { t } = useLanguage();

  const { data: availableFilters } = useQuery({
    queryKey: [getPerimeterAvailableFilters.name, perimeterFilterView, taxonomy],
    queryFn: () => getPerimeterAvailableFilters({ perimeterFilterView, taxonomy: unref(taxonomy) })
  });

  const isFilterAvailable = (
    filterName: FilterNameType,
    isLoading: Ref<boolean>,
    items: ComputedRef<TreeViewItemType | undefined> | ComputedRef<TreeView | undefined>
  ) => {
    if (!availableFilters.value || !Object.keys(availableFilters.value).includes(filterName)) {
      return false;
    }

    return items || isLoading.value;
  };

  const getFilterLabel = (filterName: FilterNameType) => {
    if (!availableFilters.value) {
      return '';
    }

    return (
      availableFilters.value[filterName as keyof typeof availableFilters.value]
        ?.map((label: string) => t(label))
        .join(' / ') ?? ''
    );
  };

  const isEnabled = (
    filterName: FilterNameType,
    filters?: Awaited<ReturnType<typeof getPerimeterAvailableFilters>>
  ) => {
    if (!filters || !Object.keys(filters).includes(filterName)) {
      return false;
    }

    if (isEnabledPredicate) {
      return isEnabledPredicate(filterName);
    }

    return true;
  };

  const isNomenclatureFiltersEnabled = computed(() => isEnabled('nomenclatureFilters', availableFilters.value));
  const nomenclatureFiltersLabel = computed(() => getFilterLabel('nomenclatureFilters'));
  const nomenclatureFilters = useNomenclatureData({
    debouncedSearch,
    enabled: isNomenclatureFiltersEnabled,
    checkedItemsState,
    taxonomy
  });
  const isNomenclatureFiltersAvailable = computed(() =>
    isFilterAvailable(
      'nomenclatureFilters',
      nomenclatureFilters.isLoadingItemsNomenclature,
      nomenclatureFilters.itemsNomenclature
    )
  );

  const isStoresFiltersEnabled = computed(() => isEnabled('storesFilters', availableFilters.value));
  const storesFiltersLabel = computed(() => getFilterLabel('storesFilters'));
  const storesFilters = useStoreData({
    debouncedSearch,
    enabled: isStoresFiltersEnabled,
    checkedItemsState,
    taxonomy
  });
  const isStoresFiltersAvailable = computed(() =>
    isFilterAvailable('storesFilters', storesFilters.isLoadingStores, storesFilters.itemsStores)
  );

  const isActivityTypesFiltersEnabled = computed(() => isEnabled('activityTypesFilters', availableFilters.value));
  const activityTypesFiltersLabel = computed(() => getFilterLabel('activityTypesFilters'));
  const activityTypesFilters = useActivityTypeData({
    debouncedSearch,
    enabled: isActivityTypesFiltersEnabled,
    checkedItemsState,
    taxonomy
  });
  const isActivityTypesFiltersAvailable = computed(() =>
    isFilterAvailable(
      'activityTypesFilters',
      activityTypesFilters.isLoadingActivityTypes,
      activityTypesFilters.itemsActivityTypes
    )
  );

  const isIntegratedFranchisedFiltersEnabled = computed(() =>
    isEnabled('integratedFranchisedFilters', availableFilters.value)
  );
  const integratedFranchisedFiltersLabel = computed(() => getFilterLabel('integratedFranchisedFilters'));
  const integratedFranchisedFilters = useIntegratedFranchisedData({
    debouncedSearch,
    enabled: isIntegratedFranchisedFiltersEnabled,
    checkedItemsState,
    taxonomy
  });
  const isIntegratedFranchisedFiltersAvailable = computed(() =>
    isFilterAvailable(
      'integratedFranchisedFilters',
      integratedFranchisedFilters.isLoadingIntegratedFranchised,
      integratedFranchisedFilters.itemsIntegratedFranchised
    )
  );

  const isTriptyqueFiltersEnabled = computed(() => isEnabled('triptyqueFilters', availableFilters.value));
  const triptyqueFiltersLabel = computed(() => getFilterLabel('triptyqueFilters'));
  const triptyqueFilters = useTriptyqueData({
    debouncedSearch,
    enabled: isTriptyqueFiltersEnabled,
    checkedItemsState,
    taxonomy
  });
  const isTriptyqueFiltersAvailable = computed(() =>
    isFilterAvailable('triptyqueFilters', triptyqueFilters.isLoadingTriptyque, triptyqueFilters.itemsTriptyque)
  );

  const isAssortmentTypologyFiltersEnabled = computed(() =>
    isEnabled('assortmentTypologyFilters', availableFilters.value)
  );
  const assortmentTypologyFiltersLabel = computed(() => getFilterLabel('assortmentTypologyFilters'));
  const assortmentTypologyFilters = useAssortmentTypologiesData({
    debouncedSearch,
    enabled: isAssortmentTypologyFiltersEnabled,
    checkedItemsState,
    taxonomy
  });
  const isAssortmentTypologyFiltersAvailable = computed(() =>
    isFilterAvailable(
      'assortmentTypologyFilters',
      assortmentTypologyFilters.isLoadingAssortmentTypology,
      assortmentTypologyFilters.itemsAssortmentTypology
    )
  );

  const isProductTypologyFiltersEnabled = computed(() => isEnabled('productTypologyFilters', availableFilters.value));
  const productTypologyFiltersLabel = computed(() => getFilterLabel('productTypologyFilters'));
  const productTypologyFilters = useProductTypologiesData({
    debouncedSearch,
    enabled: isProductTypologyFiltersEnabled,
    checkedItemsState,
    taxonomy
  });
  const isProductTypologyFiltersAvailable = computed(() =>
    isFilterAvailable(
      'productTypologyFilters',
      productTypologyFilters.isLoadingProductTypology,
      productTypologyFilters.itemsProductTypology
    )
  );

  const isBrandsFiltersEnabled = computed(() => isEnabled('brandsFilters', availableFilters.value));
  const brandsFiltersLabel = computed(() => getFilterLabel('brandsFilters'));
  const brandsFilters = useBrandData({
    debouncedSearch,
    checkedItemsState,
    enabled: isBrandsFiltersEnabled,
    taxonomy
  });
  const isBrandsFiltersAvailable = computed(() =>
    isFilterAvailable('brandsFilters', brandsFilters.isLoadingBrands, brandsFilters.itemsBrands)
  );

  const isStoresRegionFiltersEnabled = computed(() => isEnabled('storesRegionFilters', availableFilters.value));
  const storesRegionFiltersLabel = computed(() => getFilterLabel('storesRegionFilters'));
  const storesRegionFilters = useStoresRegionData({
    debouncedSearch,
    enabled: isStoresRegionFiltersEnabled,
    checkedItemsState,
    taxonomy
  });
  const isStoresRegionFiltersAvailable = computed(() =>
    isFilterAvailable(
      'storesRegionFilters',
      storesRegionFilters.isLoadingStoresRegion,
      storesRegionFilters.itemsStoresRegion
    )
  );

  const isSuppliersFiltersEnabled = computed(() => isEnabled('suppliersFilters', availableFilters.value));
  const suppliersFiltersLabel = computed(() => getFilterLabel('suppliersFilters'));
  const suppliersFilters = useSuppliersData({
    debouncedSearch,
    checkedItemsState,
    enabled: isSuppliersFiltersEnabled,
    taxonomy
  });

  const isBusinessBrandsFiltersEnabled = computed(() => isEnabled('businessBrandFilters', availableFilters.value));

  const businessBrandsFiltersLabel = computed(() => getFilterLabel('businessBrandFilters'));

  const businessBrandFilters = useBusinessBrandData({
    debouncedSearch,
    checkedItemsState,
    enabled: isBusinessBrandsFiltersEnabled,
    taxonomy
  });

  const isBusinessBrandsFiltersAvailable = computed(() =>
    isFilterAvailable('businessBrandFilters', businessBrandFilters.isLoadingBrands, businessBrandFilters.itemsBrands)
  );

  const isSuppliersFiltersAvailable = computed(() =>
    isFilterAvailable('suppliersFilters', suppliersFilters.isLoadingSuppliers, suppliersFilters.itemsSuppliers)
  );

  const isHoldingFiltersEnabled = computed(() => isEnabled('bemHoldingFilters', availableFilters.value));
  const bemHoldingFiltersLabel = computed(() => getFilterLabel('bemHoldingFilters'));
  const bemHoldingFilters = useHoldingsData({
    debouncedSearch,
    checkedItemsState,
    enabled: isHoldingFiltersEnabled,
    taxonomy,
    filterType: 'bemMainHolding',
    filterName: 'bemHoldingFilters'
  });
  const isBemHoldingFiltersAvailable = computed(() =>
    isFilterAvailable('bemHoldingFilters', bemHoldingFilters.isLoadingHoldings, bemHoldingFilters.itemsHoldings)
  );

  const isBemMerchandiseFiltersEnabled = computed(() => isEnabled('bemMerchandiseFilters', availableFilters.value));
  const bemMerchandiseFiltersLabel = computed(() => getFilterLabel('bemMerchandiseFilters'));
  const bemMerchandiseFilters = useHoldingsData({
    debouncedSearch,
    checkedItemsState,
    enabled: isBemMerchandiseFiltersEnabled,
    taxonomy,
    filterType: 'bemMerchandise',
    filterName: 'bemMerchandiseFilters'
  });
  const isBemMerchandiseFiltersAvailable = computed(() =>
    isFilterAvailable(
      'bemMerchandiseFilters',
      bemMerchandiseFilters.isLoadingHoldings,
      bemMerchandiseFilters.itemsHoldings
    )
  );

  const productsFiltersEnabled = computed(() => isEnabled(productFilterConfig.filterName, availableFilters.value));

  const productsFiltersAvailable = computed(() =>
    isFilterAvailable(productFilterConfig.filterName, productsFilters.isLoadingProducts, productsFilters.itemsProducts)
  );

  const productsFiltersLabel = computed(() => getFilterLabel(productFilterConfig.filterName));

  const productsFilters = useProductData({
    debouncedSearch,
    enabled: productsFiltersEnabled,
    productFilterConfigType: productFilterConfig,
    checkedItemsState,
    taxonomy
  });

  const isConsostyleMicroFiltersEnabled = computed(() => isEnabled('consostyleMicroFilters', availableFilters.value));
  const consostyleMicroFiltersLabel = computed(() => getFilterLabel('consostyleMicroFilters'));
  const consostyleMicroFilters = useCustomerSegmentationFilterData({
    debouncedSearch,
    enabled: isConsostyleMicroFiltersEnabled,
    checkedItemsState,
    segmentationType: CONSOSTYLE
  });
  const isConsostyleMicroFiltersAvailable = computed(() =>
    isFilterAvailable('consostyleMicroFilters', consostyleMicroFilters.isLoadingFilters, consostyleMicroFilters.items)
  );

  const isLifestageMicroFiltersEnabled = computed(() => isEnabled('lifestageMicroFilters', availableFilters.value));
  const lifestageMicroFiltersLabel = computed(() => getFilterLabel('lifestageMicroFilters'));
  const lifestageMicroFilters = useCustomerSegmentationFilterData({
    debouncedSearch,
    enabled: isLifestageMicroFiltersEnabled,
    checkedItemsState,
    segmentationType: LIFESTAGE
  });
  const isLifestageMicroFiltersAvailable = computed(() =>
    isFilterAvailable('lifestageMicroFilters', lifestageMicroFilters.isLoadingFilters, lifestageMicroFilters.items)
  );

  const data = {
    nomenclatureFilters: {
      label: nomenclatureFiltersLabel,
      items: nomenclatureFilters.itemsNomenclature,
      isLoading: nomenclatureFilters.isLoadingItemsNomenclature,
      isEnabled: isNomenclatureFiltersEnabled,
      isAvailable: isNomenclatureFiltersAvailable,
      loadingItemsCount: 1
    },
    storesFilters: {
      label: storesFiltersLabel,
      items: storesFilters.itemsStores,
      isLoading: storesFilters.isLoadingStores,
      isEnabled: isStoresFiltersEnabled,
      isAvailable: isStoresFiltersAvailable,
      loadingItemsCount: 7
    },
    activityTypesFilters: {
      label: activityTypesFiltersLabel,
      items: activityTypesFilters.itemsActivityTypes,
      isLoading: activityTypesFilters.isLoadingActivityTypes,
      isEnabled: isActivityTypesFiltersEnabled,
      isAvailable: isActivityTypesFiltersAvailable,
      loadingItemsCount: 2
    },
    integratedFranchisedFilters: {
      label: integratedFranchisedFiltersLabel,
      items: integratedFranchisedFilters.itemsIntegratedFranchised,
      isLoading: integratedFranchisedFilters.isLoadingIntegratedFranchised,
      isEnabled: isIntegratedFranchisedFiltersEnabled,
      isAvailable: isIntegratedFranchisedFiltersAvailable,
      loadingItemsCount: 2
    },
    triptyqueFilters: {
      label: triptyqueFiltersLabel,
      items: triptyqueFilters.itemsTriptyque,
      isLoading: triptyqueFilters.isLoadingTriptyque,
      isEnabled: isTriptyqueFiltersEnabled,
      isAvailable: isTriptyqueFiltersAvailable,
      loadingItemsCount: 3
    },
    assortmentTypologyFilters: {
      label: assortmentTypologyFiltersLabel,
      items: assortmentTypologyFilters.itemsAssortmentTypology,
      isLoading: assortmentTypologyFilters.isLoadingAssortmentTypology,
      isEnabled: isAssortmentTypologyFiltersEnabled,
      isAvailable: isAssortmentTypologyFiltersAvailable,
      loadingItemsCount: 5
    },
    productTypologyFilters: {
      label: productTypologyFiltersLabel,
      items: productTypologyFilters.itemsProductTypology,
      isLoading: productTypologyFilters.isLoadingProductTypology,
      isEnabled: isProductTypologyFiltersEnabled,
      isAvailable: isProductTypologyFiltersAvailable,
      loadingItemsCount: 5
    },
    brandsFilters: {
      label: brandsFiltersLabel,
      items: brandsFilters.itemsBrands,
      isLoading: brandsFilters.isLoadingBrands,
      isEnabled: isBrandsFiltersEnabled,
      isAvailable: isBrandsFiltersAvailable,
      loadingItemsCount: 6
    },
    storesRegionFilters: {
      label: storesRegionFiltersLabel,
      items: storesRegionFilters.itemsStoresRegion,
      isLoading: storesRegionFilters.isLoadingStoresRegion,
      isEnabled: isStoresRegionFiltersEnabled,
      isAvailable: isStoresRegionFiltersAvailable,
      loadingItemsCount: 6
    },
    suppliersFilters: {
      label: suppliersFiltersLabel,
      items: suppliersFilters.itemsSuppliers,
      isLoading: suppliersFilters.isLoadingSuppliers,
      isEnabled: isSuppliersFiltersEnabled,
      isAvailable: isSuppliersFiltersAvailable,
      loadingItemsCount: 6
    },
    bemHoldingFilters: {
      label: bemHoldingFiltersLabel,
      items: bemHoldingFilters.itemsHoldings,
      isLoading: bemHoldingFilters.isLoadingHoldings,
      isEnabled: isHoldingFiltersEnabled,
      isAvailable: isBemHoldingFiltersAvailable,
      loadingItemsCount: 6
    },
    bemMerchandiseFilters: {
      label: bemMerchandiseFiltersLabel,
      items: bemMerchandiseFilters.itemsHoldings,
      isLoading: bemMerchandiseFilters.isLoadingHoldings,
      isEnabled: isBemMerchandiseFiltersEnabled,
      isAvailable: isBemMerchandiseFiltersAvailable,
      loadingItemsCount: 6
    },
    [productFilterConfig.filterName]: {
      label: productsFiltersLabel,
      items: productsFilters.itemsProducts,
      isLoading: productsFilters.isLoadingProducts,
      isEnabled: productsFiltersEnabled,
      isAvailable: productsFiltersAvailable,
      loadingItemsCount: 6
    },
    consostyleMicroFilters: {
      label: consostyleMicroFiltersLabel,
      items: consostyleMicroFilters.items,
      isLoading: consostyleMicroFilters.isLoadingFilters,
      isEnabled: isConsostyleMicroFiltersEnabled,
      isAvailable: isConsostyleMicroFiltersAvailable,
      loadingItemsCount: 10
    },
    lifestageMicroFilters: {
      label: lifestageMicroFiltersLabel,
      items: lifestageMicroFilters.items,
      isLoading: lifestageMicroFilters.isLoadingFilters,
      isEnabled: isLifestageMicroFiltersEnabled,
      isAvailable: isLifestageMicroFiltersAvailable,
      loadingItemsCount: 10
    },
    businessBrandFilters: {
      label: businessBrandsFiltersLabel,
      items: businessBrandFilters.itemsBrands,
      isLoading: businessBrandFilters.isLoadingBrands,
      isEnabled: isBusinessBrandsFiltersEnabled,
      isAvailable: isBusinessBrandsFiltersAvailable,
      loadingItemsCount: 6
    }
  };

  return computed(() =>
    Object.fromEntries(
      Object.entries(data)
        .filter(([filterName]) => {
          if (!availableFilters.value || !Object.keys(availableFilters.value).includes(filterName)) {
            return false;
          }
          return true;
        })
        .sort(([a], [b]) => sortFromCompareArray(Object.keys(availableFilters.value ?? {}))(a, b))
    )
  );
};
